import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { GraphRunnerLeaderboardApi } from './leaderboardApi';
import type { GameSession, LeaderboardEntry } from './types';

export function useGraphRunnerLeaderboard() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);

  const api = useMemo(
    () =>
      new GraphRunnerLeaderboardApi({
        discoveryApi,
        fetchApi,
        identityApi,
      }),
    [discoveryApi, fetchApi, identityApi],
  );

  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [canRemoveScores, setCanRemoveScores] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshLeaderboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getLeaderboard();
      setEntries(result.entries);
      setCanRemoveScores(result.canRemoveScores);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not load the leaderboard.',
      );
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refreshLeaderboard();
  }, [refreshLeaderboard]);

  const startSession = useCallback(async (): Promise<GameSession> => {
    return api.startSession();
  }, [api]);

  const submitScore = useCallback(
    async (sessionId: string, score: number) => {
      if (score <= 0) {
        return null;
      }

      try {
        const result = await api.submitScore(sessionId, score);
        setEntries(result.entries);
        setCanRemoveScores(result.canRemoveScores);
        setError(null);
        return result;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Could not submit your score.',
        );
        return null;
      }
    },
    [api],
  );

  const deleteScore = useCallback(
    async (userEntityRef: string) => {
      try {
        const result = await api.deleteScore(userEntityRef);
        setEntries(result.entries);
        setCanRemoveScores(result.canRemoveScores);
        setError(null);
        return true;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Could not remove that score.',
        );
        return false;
      }
    },
    [api],
  );

  return {
    entries,
    canRemoveScores,
    loading,
    error,
    startSession,
    submitScore,
    deleteScore,
  };
}
